import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { rbacService } from "../../services/rbacService";
import {
  Role,
  Permission,
  UserRole,
  RoleFormData,
  AssignRoleData,
  UserPermissionsSummary,
  PermissionModule,
} from "../../types/rbac";

interface RbacState {
  roles: Role[];
  permissions: Permission[];
  permissionsByModule: PermissionModule;
  userRoles: Record<string, UserRole[]>;
  userPermissions: Record<string, UserPermissionsSummary>;
  isLoading: boolean;
  error: string | null;
  selectedRole: Role | null;
  selectedUser: string | null;
}

const initialState: RbacState = {
  roles: [],
  permissions: [],
  permissionsByModule: {},
  userRoles: {},
  userPermissions: {},
  isLoading: false,
  error: null,
  selectedRole: null,
  selectedUser: null,
};

// Async thunks
export const fetchRoles = createAsyncThunk("rbac/fetchRoles", async () => {
  return await rbacService.getAllRoles();
});

export const fetchPermissions = createAsyncThunk(
  "rbac/fetchPermissions",
  async () => {
    return await rbacService.getAllPermissions();
  }
);

export const createRole = createAsyncThunk(
  "rbac/createRole",
  async (roleData: RoleFormData) => {
    return await rbacService.createRole(roleData);
  }
);

export const updateRole = createAsyncThunk(
  "rbac/updateRole",
  async ({ id, roleData }: { id: string; roleData: Partial<RoleFormData> }) => {
    return await rbacService.updateRole(id, roleData);
  }
);

export const deleteRole = createAsyncThunk(
  "rbac/deleteRole",
  async (id: string) => {
    await rbacService.deleteRole(id);
    return id;
  }
);

export const fetchUserRoles = createAsyncThunk(
  "rbac/fetchUserRoles",
  async (userId: string) => {
    return await rbacService.getUserRoles(userId);
  }
);

export const assignRoleToUser = createAsyncThunk(
  "rbac/assignRoleToUser",
  async (assignData: AssignRoleData) => {
    return await rbacService.assignRoleToUser(assignData);
  }
);

export const removeRoleFromUser = createAsyncThunk(
  "rbac/removeRoleFromUser",
  async (assignData: AssignRoleData) => {
    await rbacService.removeRoleFromUser(assignData);
    return assignData;
  }
);

export const fetchUserPermissionsSummary = createAsyncThunk(
  "rbac/fetchUserPermissionsSummary",
  async (userId: string) => {
    return await rbacService.getUserPermissionsSummary(userId);
  }
);

const rbacSlice = createSlice({
  name: "rbac",
  initialState,
  reducers: {
    setSelectedRole: (state, action: PayloadAction<Role | null>) => {
      state.selectedRole = action.payload;
    },
    setSelectedUser: (state, action: PayloadAction<string | null>) => {
      state.selectedUser = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Roles
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles = action.payload;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch roles";
      });

    // Fetch Permissions
    builder
      .addCase(fetchPermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions = action.payload.permissions;
        state.permissionsByModule = action.payload.permissionsByModule;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch permissions";
      });

    // Create Role
    builder
      .addCase(createRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles.push(action.payload);
      })
      .addCase(createRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to create role";
      });

    // Update Role
    builder
      .addCase(updateRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.roles.findIndex(
          (role) => role.id === action.payload.id
        );
        if (index !== -1) {
          state.roles[index] = action.payload;
        }
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to update role";
      });

    // Delete Role
    builder
      .addCase(deleteRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles = state.roles.filter((role) => role.id !== action.payload);
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to delete role";
      });

    // Fetch User Roles
    builder
      .addCase(fetchUserRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        const userId = action.meta.arg;
        state.userRoles[userId] = action.payload;
      })
      .addCase(fetchUserRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch user roles";
      });

    // Assign Role to User
    builder
      .addCase(assignRoleToUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(assignRoleToUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const userId = action.payload.user_id;
        if (!state.userRoles[userId]) {
          state.userRoles[userId] = [];
        }
        state.userRoles[userId].push(action.payload);
      })
      .addCase(assignRoleToUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to assign role to user";
      });

    // Remove Role from User
    builder
      .addCase(removeRoleFromUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeRoleFromUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const { userId, roleId, branchId } = action.payload;
        if (state.userRoles[userId]) {
          state.userRoles[userId] = state.userRoles[userId].filter(
            (userRole) =>
              !(userRole.role_id === roleId && userRole.branch_id === branchId)
          );
        }
      })
      .addCase(removeRoleFromUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to remove role from user";
      });

    // Fetch User Permissions Summary
    builder
      .addCase(fetchUserPermissionsSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserPermissionsSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        const userId = action.meta.arg;
        state.userPermissions[userId] = action.payload;
      })
      .addCase(fetchUserPermissionsSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.error.message || "Failed to fetch user permissions summary";
      });
  },
});

export const { setSelectedRole, setSelectedUser, clearError } =
  rbacSlice.actions;
export default rbacSlice.reducer;
